import './newrelic.ts';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@interstate/components/InterstateTheming/index.css';
import './index.css';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';
import { msalInstance } from './auth/msalConfig.ts';
import { consumeReturnPath } from './auth/session.ts';

async function bootstrap(): Promise<void> {
  await msalInstance.initialize();

  // Process a returning redirect before anything renders. MsalProvider would do this too, but we
  // need the result here to restore the pre-expiry URL below.
  const redirectResult = await msalInstance.handleRedirectPromise();
  if (redirectResult?.account) {
    msalInstance.setActiveAccount(redirectResult.account);
  }

  // Restore the active account on load (e.g. a fresh tab reading shared localStorage)
  // so getActiveAccount() is populated for token acquisition and a clean single-account logout.
  if (!msalInstance.getActiveAccount()) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  // Keep the active account current after a login redirect completes. ACQUIRE_TOKEN_SUCCESS covers
  // re-authentication, which fires that instead of LOGIN_SUCCESS because an account already existed.
  msalInstance.addEventCallback((event) => {
    if ((event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) && event.payload) {
      const account = (event.payload as AuthenticationResult).account;
      if (account) msalInstance.setActiveAccount(account);
    }
  });

  // Entra redirects back to redirectUri (the origin), not the deep link the user was on, so put the
  // URL back before the router reads it. App is imported dynamically for exactly this reason: it
  // builds its browser router at module scope, which snapshots window.location on import.
  // Only on a return from sign-in: the reload that follows an expiry sign-out must keep the path
  // for the sign-in that comes after it.
  const returnTo = redirectResult ? consumeReturnPath() : null;
  if (returnTo) {
    window.history.replaceState(null, '', returnTo);
  }

  const { default: App } = await import('./App.tsx');

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}

void bootstrap();
